import {
  Check,
  CircleCheck,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { apiError, useLocale, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { Button, Input, Modal } from "./ui";
import type { TourDetail } from "../../shared/api-types";

// Ein Dialog für den ganzen Weg nach draußen: Stand & Veröffentlichen, Link &
// Embed, Passwort. Veröffentlicht wird nur hier — der Button auf der Tour-Seite
// öffnet bloß den Dialog. Passwort geht schon vor dem Veröffentlichen und wirkt
// sofort (der Viewer prüft es live, es steht nicht im Snapshot).

export function ShareModal({
  tour,
  open,
  onClose,
  onChanged,
}: {
  tour: TourDetail;
  open: boolean;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const t = useT();
  const [locale] = useLocale();
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState(tour.password ?? "");
  const [showPw, setShowPw] = useState(false);
  // Gesetztes Passwort steht im Feld, damit man es beim erneuten Öffnen einsehen kann
  useEffect(() => {
    setPw(tour.password ?? "");
    setShowPw(false);
  }, [tour.password, open]);
  const [copied, setCopied] = useState<"link" | "embed" | null>(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const published = tour.status === "published";
  const hasReady = tour.scenes.some((s) => s.status === "ready");
  // Eine Szene, die schon draußen ist, aber gerade verarbeitet wird (Bild ersetzt) oder
  // fehlgeschlagen ist, fiele beim Veröffentlichen aus der Tour — mitsamt aller Hotspots,
  // die auf sie zeigen. Der Server lehnt das ab; hier gibt es den Grund dazu.
  const blocked = tour.scenes.some(
    (s) => tour.publishedSceneIds.includes(s.id) && s.status !== "ready",
  );
  const publicUrl = `${window.location.origin}/t/${tour.slug}`;
  // magnetometer gehört zwingend dazu: WebKit gibt DeviceOrientation in einem
  // fremden iframe nur frei, wenn gyroscope UND accelerometer UND magnetometer
  // erlaubt sind (LocalDOMWindow::isAllowedToUseDeviceOrientation). Fehlt eines,
  // liefert requestPermission() sofort "denied" — ohne Abfrage, ohne Fehler.
  const embedCode = `<iframe src="${window.location.origin}/embed/${tour.slug}" style="width:100%;aspect-ratio:16/9;border:0;border-radius:12px" allow="fullscreen; gyroscope; accelerometer; magnetometer; xr-spatial-tracking" allowfullscreen loading="lazy" title="${tour.name}"></iframe>`;

  function copy(text: string, which: "link" | "embed") {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    });
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (e) {
      setError(apiError(t, (e as Error).message));
    }
    setBusy(false);
  }

  const publish = () => run(() => apiJson(`/api/tours/${tour.id}/publish`, { method: "POST" }));
  const unpublish = () =>
    run(async () => {
      await apiJson(`/api/tours/${tour.id}/unpublish`, { method: "POST" });
      setConfirmUnpublish(false);
      onClose();
    });
  const savePassword = () =>
    run(async () => {
      await apiJson(`/api/tours/${tour.id}`, { method: "PATCH", json: { password: pw } });
    });
  const removePassword = () =>
    run(() => apiJson(`/api/tours/${tour.id}`, { method: "PATCH", json: { password: null } }));

  if (confirmUnpublish) {
    return (
      <Modal open={open} onClose={() => setConfirmUnpublish(false)} title={t.share.unpublishTitle}>
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">{t.share.unpublishText}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmUnpublish(false)}>
            {t.common.cancel}
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => void unpublish()}>
            {t.share.unpublish}
          </Button>
        </div>
      </Modal>
    );
  }

  const sectionLabel = "mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300";
  const hint = "text-xs text-zinc-400 dark:text-zinc-500";

  return (
    <Modal open={open} onClose={onClose} title={t.share.title} wide>
      <div className="space-y-5">
        {/* Stand & Veröffentlichen */}
        <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800">
          {published ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-zinc-600 dark:text-zinc-300">
                  {t.share.publishedState(
                    new Date(tour.publishedAt!).toLocaleString(
                      locale === "de" ? "de-DE" : "en-US",
                      {
                        dateStyle: "medium",
                        timeStyle: "short",
                      },
                    ),
                  )}
                </span>
                {tour.hasChanges ? (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">
                    {t.share.pendingChanges}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400">
                    <CircleCheck className="size-3" />
                    {t.share.upToDate}
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {tour.hasChanges && (
                  <Button disabled={busy || blocked} onClick={() => void publish()}>
                    <RefreshCw className="size-3.5" />
                    {t.share.republish}
                  </Button>
                )}
                <p className={hint}>{blocked ? t.share.blockedHint : t.share.republishHint}</p>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button disabled={busy || !hasReady} onClick={() => void publish()}>
                <Globe className="size-3.5" />
                {t.share.publish}
              </Button>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {hasReady ? t.share.draftState : t.share.draftNoScene}
              </p>
            </div>
          )}
        </div>

        {published ? (
          <>
            <div>
              <span className={sectionLabel}>{t.share.publicLink}</span>
              <div className="flex gap-2">
                <Input readOnly className="w-full font-mono text-xs" value={publicUrl} />
                <Button
                  variant="ghost"
                  className="shrink-0 px-3"
                  onClick={() => copy(publicUrl, "link")}
                >
                  {copied === "link" ? (
                    <Check className="size-4 text-emerald-500" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={t.share.openTour}
                  className="inline-flex h-9 items-center rounded-lg border border-zinc-200 px-3 text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  <ExternalLink className="size-4" />
                </a>
              </div>
            </div>

            <div>
              <span className={sectionLabel}>{t.share.embedCode}</span>
              <div className="flex gap-2">
                <textarea
                  readOnly
                  rows={4}
                  className="w-full resize-none rounded-lg border border-zinc-200 bg-white p-2.5 font-mono text-xs text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                  value={embedCode}
                />
                <Button
                  variant="ghost"
                  className="shrink-0 self-start px-3"
                  onClick={() => copy(embedCode, "embed")}
                >
                  {copied === "embed" ? (
                    <Check className="size-4 text-emerald-500" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <p className={hint}>{t.share.linksAfterPublish}</p>
        )}

        <div>
          <span className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            {t.share.password}
          </span>
          <p className={`mb-2 ${hint}`}>
            {tour.passwordProtected ? t.share.passwordSet : t.share.passwordHint}
          </p>
          <div className="flex gap-2">
            <div className="relative w-full">
              <Input
                type={showPw ? "text" : "password"}
                className="w-full pr-10"
                placeholder={t.share.passwordPlaceholder}
                value={pw}
                minLength={4}
                autoComplete="off"
                onChange={(e) => setPw(e.target.value)}
              />
              <button
                type="button"
                title={showPw ? t.share.hidePassword : t.share.showPassword}
                aria-label={showPw ? t.share.hidePassword : t.share.showPassword}
                onClick={() => setShowPw((v) => !v)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-zinc-400 transition-colors hover:text-zinc-700 dark:hover:text-zinc-200"
              >
                {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <Button
              variant="ghost"
              className="shrink-0"
              disabled={busy || pw.length < 4 || pw === tour.password}
              onClick={() => void savePassword()}
            >
              {t.share.passwordSave}
            </Button>
            {tour.passwordProtected && (
              <Button
                variant="danger"
                className="shrink-0"
                disabled={busy}
                onClick={() => void removePassword()}
              >
                {t.share.passwordRemove}
              </Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div
          className={`flex border-t border-zinc-100 pt-4 dark:border-zinc-800 ${
            published ? "justify-between" : "justify-end"
          }`}
        >
          {published && (
            <Button variant="danger" disabled={busy} onClick={() => setConfirmUnpublish(true)}>
              {t.share.unpublish}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            OK
          </Button>
        </div>
      </div>
    </Modal>
  );
}
